/**
 * Report Search Utility Functions
 * Provides functionality to search for reports by name across modules
 * @author Assistant on 01/27/2025
 */

// Interface for the report object structure
export interface SavedReport {
    id: number;
    reportId: number;
    reportName: string;
    reportDesc: string;
    createdAt: string;
    isSummarized: number;
    parentModuleId: number;
    isAdminReport: number;
    reportFrom: number;
    dimensionReport: number;
    createdBy: string;
    favouriteReports: any[];
    createdByUser: {
        id: number;
        displayName: string;
    } | null;
    isFavourite: number;
    childReport: number;
    // Optional properties for grouping by parent module
    parentModuleName?: string;
}

// Interface for module structure
export interface Module {
    count: number;
    id: number;
    reportName: string;
    isDefaultSummarized: number;
    savedReports: SavedReport[];
    reportAutomations: any[];
}

// Interface for the main data structure (updated for your API structure)
export interface ReportData {
    modules: Module[];
}

/**
 * Search for reports by name across all modules
 * @param data - The complete report data object
 * @param searchTerm - The report name to search for (case-insensitive)
 * @returns Array of matching report objects
 */
export function searchReportsByName(data: ReportData, searchTerm: string): SavedReport[] {
    const matchingReports: SavedReport[] = [];
    
    if (!data?.modules || !Array.isArray(data.modules)) {
        return matchingReports;
    }
    
    const searchLower = searchTerm.toLowerCase().trim();
    
    // Iterate through all modules
    data.modules.forEach(module => {
        if (module.savedReports && Array.isArray(module.savedReports)) {
            // Search through savedReports in each module
            module.savedReports.forEach(report => {
                if (report.reportName && 
                    report.reportName.toLowerCase().includes(searchLower)) {
                    matchingReports.push(report);
                }
            });
        }
    });
    
    return matchingReports;
}

/**
 * Search for reports by name with exact match
 * @param data - The complete report data object
 * @param searchTerm - The exact report name to search for (case-insensitive)
 * @returns Array of matching report objects
 */
export function searchReportsByExactName(data: ReportData, searchTerm: string): SavedReport[] {
    const matchingReports: SavedReport[] = [];
    
    if (!data?.modules || !Array.isArray(data.modules)) {
        return matchingReports;
    }
    
    const searchLower = searchTerm.toLowerCase().trim();
    
    // Iterate through all modules
    data.modules.forEach(module => {
        if (module.savedReports && Array.isArray(module.savedReports)) {
            // Search through savedReports in each module
            module.savedReports.forEach(report => {
                if (report.reportName && 
                    report.reportName.toLowerCase() === searchLower) {
                    matchingReports.push(report);
                }
            });
        }
    });
    
    return matchingReports;
}

/**
 * Search for reports by name and return with module information
 * @param data - The complete report data object
 * @param searchTerm - The report name to search for (case-insensitive)
 * @returns Array of matching report objects with module info
 */
export function searchReportsWithModuleInfo(data: ReportData, searchTerm: string): Array<SavedReport & { moduleName: string; moduleId: number }> {
    const matchingReports: Array<SavedReport & { moduleName: string; moduleId: number }> = [];
    
    if (!data?.modules || !Array.isArray(data.modules)) {
        return matchingReports;
    }
    
    const searchLower = searchTerm.toLowerCase().trim();
    
    // Iterate through all modules
    data.modules.forEach(module => {
        if (module.savedReports && Array.isArray(module.savedReports)) {
            // Search through savedReports in each module
            module.savedReports.forEach(report => {
                if (report.reportName && 
                    report.reportName.toLowerCase().includes(searchLower)) {
                    matchingReports.push({
                        ...report,
                        moduleName: module.reportName,
                        moduleId: module.id
                    });
                }
            });
        }
    });
    
    return matchingReports;
}

/**
 * Get all reports from a specific module
 * @param data - The complete report data object
 * @param moduleId - The module ID to search in
 * @returns Array of report objects from the specified module
 */
export function getReportsByModuleId(data: ReportData, moduleId: number): SavedReport[] {
    const matchingReports: SavedReport[] = [];
    
    if (!data?.modules || !Array.isArray(data.modules)) {
        return matchingReports;
    }
    
    const module = data.modules.find(m => m.id === moduleId);
    if (module && module.savedReports && Array.isArray(module.savedReports)) {
        matchingReports.push(...module.savedReports);
    }
    
    return matchingReports;
} 

/**
 * Search for reports by name and group them by module
 * @param data - The complete report data object
 * @param searchTerm - The report name to search for (case-insensitive)
 * @returns Object with module names as keys and arrays of matching reports as values
 */
export function searchReportsWithModuleGrouping(data: ReportData, searchTerm: string): { [moduleName: string]: SavedReport[] } {
    const groupedResults: { [moduleName: string]: SavedReport[] } = {};
    
    if (!data?.modules || !Array.isArray(data.modules)) {
        return groupedResults;
    }
    
    const searchLower = searchTerm.toLowerCase().trim();
    
    // Iterate through all modules
    data.modules.forEach(module => {
        if (module.savedReports && Array.isArray(module.savedReports)) {
            const matchingReports: SavedReport[] = [];
            
            // Search through savedReports in each module
            module.savedReports.forEach(report => {
                if (report.reportName && 
                    report.reportName.toLowerCase().includes(searchLower)) {
                    matchingReports.push(report);
                }
            });
            
            // Only add module to results if it has matching reports
            if (matchingReports.length > 0) {
                groupedResults[module.reportName] = matchingReports;
            }
        }
    });
    
    return groupedResults;
}

/**
 * Resolve report-details route params from a saved report item.
 * Matches navigation used across landing, header search, and company reports menu.
 */
export function getReportRouteParams(item: any, module?: any): [number, number] {
    if (item?.childReport === 1) {
        return [Number(item.id), 0];
    }

    const moduleId = Number(item.reportId ?? item.parentModuleId ?? module?.id);

    if (item?.reportFrom === 1) {
        return [moduleId, 0];
    }

    return [moduleId, Number(item.id)];
}